// client/src/components/pages/TelegramBI/Analytics/QueryPerformanceChart.js
import React, { useState, useEffect, useRef } from 'react';
import { FaChartLine, FaDatabase } from 'react-icons/fa';

const QueryPerformanceChart = ({ data, loading }) => {
  const [mounted, setMounted] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const chartRef = useRef(null);

  // Trigger mount animation
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Reset animation on data change
  useEffect(() => {
    setMounted(false);
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, [data]);

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-gray-200 p-5">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-40" />
          <div className="h-56 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  const trendData = Array.isArray(data) ? data : [];

  if (trendData.length === 0) {
    return (
      <div className="bg-surface rounded-lg border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FaChartLine className="text-primary-500" />
            Query Performance
          </h3>
        </div>
        <div className="px-5 py-16 text-center">
          <FaDatabase className="mx-auto text-3xl text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">No execution data available</p>
          <p className="text-xs text-gray-400 mt-1">
            Performance trends will appear once queries are executed
          </p>
        </div>
      </div>
    );
  }

  const maxExecutions = Math.max(...trendData.map((d) => d.executions || 0), 1);
  const maxAvgTime = Math.max(...trendData.map((d) => d.avg_time || 0), 1);

  // Calculate SVG polyline points for the avg time line
  const chartWidth = 100; // percentage-based
  const chartHeight = 180;
  const padding = { top: 10, right: 10, bottom: 0, left: 10 };
  const innerWidth = chartWidth;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  const linePoints = trendData
    .map((d, i) => {
      const x = trendData.length === 1 ? 50 : (i / (trendData.length - 1)) * 100;
      const y = padding.top + innerHeight - ((d.avg_time || 0) / maxAvgTime) * innerHeight;
      return `${x},${y}`;
    })
    .join(' ');

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Show a subset of x-axis labels to avoid crowding
  const labelInterval = Math.max(1, Math.ceil(trendData.length / 7));

  return (
    <div className="bg-surface rounded-lg border border-gray-200">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <FaChartLine className="text-primary-500" />
          Query Performance
        </h3>
        <div className="flex items-center gap-4 text-[11px] text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-primary-500 inline-block" />
            Executions
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-0.5 bg-amber-500 inline-block rounded" />
            Avg Time
          </span>
        </div>
      </div>
      <div className="p-5">
        {/* Chart Container */}
        <div className="relative" ref={chartRef}>
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 bottom-8 w-10 flex flex-col justify-between text-[10px] text-gray-400 pointer-events-none z-10">
            <span>{maxExecutions}</span>
            <span>{Math.round(maxExecutions / 2)}</span>
            <span>0</span>
          </div>

          {/* Bar chart area */}
          <div className="ml-12 mr-2">
            <div className="relative" style={{ height: `${chartHeight}px` }}>
              {/* Horizontal grid lines */}
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="border-b border-gray-100 border-dashed" />
                ))}
              </div>

              {/* Bars */}
              <div className="absolute inset-0 flex items-end gap-0.5 px-0.5">
                {trendData.map((point, index) => {
                  const barHeight = mounted
                    ? Math.max(((point.executions || 0) / maxExecutions) * 100, 2)
                    : 0;
                  const isHovered = hoveredIndex === index;
                  return (
                    <div
                      key={index}
                      className="flex-1 relative group"
                      style={{ height: '100%' }}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                        <div
                          className={`w-full rounded-t transition-all duration-700 ease-out cursor-pointer ${
                            isHovered ? 'bg-primary-600' : 'bg-primary-500'
                          }`}
                          style={{
                            height: `${barHeight}%`,
                            maxHeight: `${chartHeight}px`,
                            transitionDelay: `${index * 30}ms`,
                          }}
                        />
                      </div>

                      {/* Tooltip */}
                      {isHovered && (
                        <div
                          className="absolute z-20 bg-gray-900 dark:bg-gray-50 text-white text-xs rounded-lg px-3 py-2 shadow-lg pointer-events-none whitespace-nowrap"
                          style={{
                            bottom: `${barHeight + 5}%`,
                            left: '50%',
                            transform: 'translateX(-50%)',
                          }}
                        >
                          <div className="font-medium mb-1">{formatDate(point.date)}</div>
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-sm bg-primary-400 inline-block" />
                            <span>{point.executions || 0} executions</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-sm bg-amber-400 inline-block" />
                            <span>{(point.avg_time || 0).toFixed(0)}ms avg</span>
                          </div>
                          {point.successful != null && (
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-sm bg-emerald-400 inline-block" />
                              <span>{point.successful} successful</span>
                            </div>
                          )}
                          <div
                            className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0"
                            style={{
                              borderLeft: '4px solid transparent',
                              borderRight: '4px solid transparent',
                              borderTop: '4px solid #111827',
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* SVG overlay for avg time line */}
              {trendData.some((d) => d.avg_time > 0) && (
                <svg
                  className="absolute inset-0 pointer-events-none"
                  viewBox={`0 0 100 ${chartHeight}`}
                  preserveAspectRatio="none"
                  style={{ width: '100%', height: '100%' }}
                >
                  <polyline
                    points={linePoints}
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    className={`transition-all duration-1000 ${mounted ? 'opacity-100' : 'opacity-0'}`}
                  />
                  {trendData.map((d, i) => {
                    const x = trendData.length === 1 ? 50 : (i / (trendData.length - 1)) * 100;
                    const y =
                      padding.top + innerHeight - ((d.avg_time || 0) / maxAvgTime) * innerHeight;
                    return (
                      <circle
                        key={i}
                        cx={x}
                        cy={y}
                        r="2"
                        fill="#f59e0b"
                        vectorEffect="non-scaling-stroke"
                        className={`transition-all duration-1000 ${mounted ? 'opacity-100' : 'opacity-0'}`}
                        style={{ transitionDelay: `${i * 30 + 300}ms` }}
                      />
                    );
                  })}
                </svg>
              )}
            </div>

            {/* X-axis labels */}
            <div className="flex mt-2">
              {trendData.map((point, index) => (
                <div key={index} className="flex-1 text-center">
                  {index % labelInterval === 0 && (
                    <span className="text-[10px] text-gray-400">{formatDate(point.date)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right Y-axis label for avg time */}
          {trendData.some((d) => d.avg_time > 0) && (
            <div className="absolute right-0 top-0 bottom-8 w-10 flex flex-col justify-between text-[10px] text-amber-500 text-right pointer-events-none">
              <span>{maxAvgTime.toFixed(0)}ms</span>
              <span>{(maxAvgTime / 2).toFixed(0)}ms</span>
              <span>0ms</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueryPerformanceChart;
